import { capitalize } from 'lodash-es';
import { useEffect, useRef, useState } from 'react';

import { VFlex } from '../VFlex';
import { VLabel } from '../VLabel';
import { BASE_ORIENTATION } from '../constant';
import { new__cn as cn } from '../util';
import { ConditionalTooltip } from './ConditionalTooltip';
import { useBaseInput } from './hook';
import twStyle, { getGap } from './style';
import type { VBaseInputProps } from './type';

const VBaseInput = (
  props: React.PropsWithChildren<VBaseInputProps> &
    React.RefAttributes<HTMLDivElement>,
) => {
  const {
    ref,
    children,

    className,
    comment,
    disabled,
    id: _id,
    label,
    labelInfoTooltipContent,
    orientation,
    readOnly = false,
    required,
    valid,
  } = props;
  const { id, status, setId, setRequired, setStatus } = useBaseInput();
  const style = twStyle({ ...props, status });
  const labelWrapperRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const isHorizontal = orientation === BASE_ORIENTATION.HORIZONTAL;
  const gap = getGap({ orientation, size: props.size });
  const gapInVFlexUnits = gap / 16;
  const labelContent =
    label != null ? (typeof label === 'string' ? label : label()) : null;

  useEffect(() => {
    setRequired(required);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [required]);

  useEffect(() => {
    if (_id) setId(_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_id]);

  useEffect(() => {
    setStatus(prev => ({ ...prev, disabled, valid, readOnly }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, valid, readOnly]);

  useEffect(() => {
    if (!labelWrapperRef.current) return;

    const labelEl = labelWrapperRef.current.querySelector('label');

    if (!labelEl) return;

    setIsOverflowing(labelEl.clientWidth < labelEl.scrollWidth);
  }, [label, isHorizontal]);

  return (
    <VFlex
      data-testid={`v-base-input-${id}-container`}
      aria-label={`v-base-input-${label}`}
      ref={ref}
      className={cn(style.container, className)}
      role="textbox"
      direction="col"
      gap={0.25}
    >
      <VFlex
        data-testid={`v-base-input-${id}-textbox-wrapper`}
        role="presentation"
        className={style.textboxWrapper}
        gap={gapInVFlexUnits}
      >
        {labelContent && (
          <ConditionalTooltip
            toShowTooltip={isOverflowing}
            content={labelContent}
          >
            <VLabel
              data-testid={`v-base-input-${id}-label`}
              className="max-w-full overflow-hidden text-ellipsis whitespace-nowrap"
              ref={labelWrapperRef}
              required={required ?? false}
              disabled={status.disabled ?? false}
              htmlFor={id}
              infoTooltipContent={labelInfoTooltipContent}
              labelClassName={cn(
                style.labelWrapper,
                'overflow-hidden text-ellipsis whitespace-nowrap',
              )}
              style={{
                maxWidth: isHorizontal
                  ? `calc(100% / 3 - ${gap}px)`
                  : undefined,
              }}
            >
              {labelContent}
            </VLabel>
          </ConditionalTooltip>
        )}
        <VFlex
          data-testid={`v-base-input-${id}-wrapper`}
          className="flex-1"
          role="presentation"
          items="center"
          gap={0.25}
        >
          {children}
        </VFlex>
      </VFlex>
      {comment && (
        <div
          data-testid={`v-base-input-${id}-comment`}
          className={style.comment}
        >
          {typeof comment === 'function'
            ? comment(status)
            : capitalize(comment)}
        </div>
      )}
    </VFlex>
  );
};

export default VBaseInput;
